'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export type MicrophonePermissionState = PermissionState | 'unsupported' | 'unknown';

interface MicrophonePermissionModalProps {
  open: boolean;
  permissionState: MicrophonePermissionState;
  isRequesting?: boolean;
  errorMessage?: string | null;
  copyOverrides?: Partial<Record<MicrophonePermissionState, Partial<PermissionCopy>>>;
  onRequestPermission: () => void;
  onClose: () => void;
}

interface PermissionCopy {
  title: string;
  description: string;
  actionLabel: string;
  canRequest: boolean;
  steps: string[];
}

const PERMISSION_COPY: Record<MicrophonePermissionState, PermissionCopy> = {
  granted: {
    title: 'Microphone Enabled',
    description: 'Your student can speak and get real-time guidance.',
    actionLabel: 'Start Live Tutor',
    canRequest: false,
    steps: [
      'Press "Start Live Tutor" to begin.',
    ],
  },
  prompt: {
    title: 'Microphone Access Needed',
    description: 'Live Tutor uses voice to guide your student.',
    actionLabel: 'Allow Microphone',
    canRequest: true,
    steps: [
      'Click "Allow Microphone" below.',
      'Choose "Allow" in the browser popup.',
    ],
  },
  denied: {
    title: 'Microphone Is Blocked',
    description: 'Re-enable mic access in your browser settings to use Live Tutor.',
    actionLabel: 'Check Again',
    canRequest: true,
    steps: [
      'Click the lock icon next to the URL.',
      'Set Microphone to "Allow".',
      'Reload the page.',
    ],
  },
  unsupported: {
    title: 'Microphone Not Available',
    description: 'Live Tutor requires a supported browser.',
    actionLabel: 'Understood',
    canRequest: false,
    steps: [
      'Use the latest Chrome, Edge, or Safari.',
      'Make sure the site is loaded over HTTPS.',
      'Check that a microphone is connected.',
    ],
  },
  unknown: {
    title: 'Microphone Access Needed',
    description: 'Live Tutor uses voice to guide your student.',
    actionLabel: 'Allow Microphone',
    canRequest: true,
    steps: [
      'Click "Allow Microphone" below.',
      'Approve in the browser dialog.',
    ],
  },
};

export default function MicrophonePermissionModal({
  open,
  permissionState,
  isRequesting = false,
  errorMessage,
  copyOverrides,
  onRequestPermission,
  onClose,
}: MicrophonePermissionModalProps) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEsc);
    };
  }, [open, onClose]);

  if (!open || !isMounted) return null;

  const copy = {
    ...PERMISSION_COPY[permissionState],
    ...(copyOverrides?.[permissionState] || {}),
  };

  return createPortal(
    <div className="fixed inset-0 z-[1400] flex items-center justify-center px-4 py-6">
      <button
        type="button"
        aria-label="Close microphone permission modal"
        onClick={onClose}
        className="absolute inset-0 bg-[color:var(--gray-900)]/45 backdrop-blur-sm"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="microphone-permission-title"
        className="relative w-full max-w-md rounded-3xl border border-[color:var(--surface-3)] bg-[color:var(--surface-0)] p-6 shadow-[0_30px_80px_rgba(15,23,42,0.24)]"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full border border-[color:var(--surface-3)] bg-[color:var(--surface-0)] p-2 text-[color:var(--gray-500)] transition hover:border-[color:var(--primary-200)] hover:text-[color:var(--primary-600)] cursor-pointer"
        >
          <X className="h-4 w-4" />
        </button>

        <h2 id="microphone-permission-title" className="mt-4 font-display text-2xl font-semibold text-[color:var(--gray-900)]">
          {copy.title}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-[color:var(--gray-600)]">
          {copy.description}
        </p>

        <div className="mt-4 rounded-2xl border border-[color:var(--surface-3)] bg-[color:var(--surface-1)] px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[color:var(--gray-500)]">Quick Setup</p>
          <ol className="mt-2 space-y-2 text-sm text-[color:var(--gray-700)]">
            {copy.steps.map((step, index) => (
              <li key={step} className="flex items-start gap-2">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-[color:var(--gray-500)]">
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>

        {errorMessage && (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
            {errorMessage}
          </div>
        )}

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-[color:var(--surface-3)] bg-[color:var(--surface-0)] px-4 py-2.5 text-sm font-semibold text-[color:var(--gray-700)] transition hover:bg-[color:var(--surface-2)] cursor-pointer"
          >
            Not Now
          </button>
          <button
            type="button"
            onClick={copy.canRequest ? onRequestPermission : onClose}
            disabled={isRequesting}
            className="flex-1 rounded-xl bg-[color:var(--primary-500)] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[color:var(--primary-600)] disabled:cursor-not-allowed disabled:opacity-70 cursor-pointer"
          >
            {isRequesting ? 'Checking...' : copy.actionLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
